import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import { useReportAccessSecondaryCategoryListPresenter } from './useReportAccessSecondaryCategoryListPresenter'
import { ReportAccessSecondaryCategoryListView } from '../../views/ReportAccessSecondaryCategoryListView'

export interface IReportAccessSecondaryCategoryListPresenterProps {
  primaryCategoryId: number
  primaryCategoryName: string
  /** Owned by the consumer, which reads the displayed page to pick defaults */
  page: number
  selectedSecondaryCategoryId: number | undefined
  isAllSecondaryCategoriesSelected: boolean
  onSelect: (config: SecondaryCategoryConfig) => void
  onToggleSelectAll: () => void
  onExitSelectAll: () => void
  onPageChange: (page: number) => void
}

/**
 * Search is scoped to one PrimaryCategory: render it with
 * `key={primaryCategoryId}` so a new PrimaryCategory starts fresh, and reset
 * the consumer-owned `page` to 1 along with it.
 */
export const ReportAccessSecondaryCategoryListPresenter = ({
  primaryCategoryId,
  primaryCategoryName,
  page,
  selectedSecondaryCategoryId,
  isAllSecondaryCategoriesSelected,
  onSelect,
  onToggleSelectAll,
  onExitSelectAll,
  onPageChange,
}: IReportAccessSecondaryCategoryListPresenterProps) => {
  const {
    data: { rows, currentPage, numPages, searchTerm },
    states: {
      showInitialLoader,
      showErrorState,
      showEmptyState,
      showEmptyStateContent,
      showList,
      isRefetching,
      isSelectAllBarVisible,
    },
    methods: {
      onSearchChange,
      onPageChange: handlePageChange,
      onRowClick,
      onToggleSelectAll: handleToggleSelectAll,
      onRetry,
    },
  } = useReportAccessSecondaryCategoryListPresenter({
    primaryCategoryId,
    page,
    selectedSecondaryCategoryId,
    isAllSecondaryCategoriesSelected,
    onSelect,
    onToggleSelectAll,
    onExitSelectAll,
    onPageChange,
  })

  return (
    <ReportAccessSecondaryCategoryListView
      primaryCategoryName={primaryCategoryName}
      rows={rows}
      isAllSecondaryCategoriesSelected={isAllSecondaryCategoriesSelected}
      isSelectAllBarVisible={isSelectAllBarVisible}
      showInitialLoader={showInitialLoader}
      showErrorState={showErrorState}
      showEmptyState={showEmptyState}
      showEmptyStateContent={showEmptyStateContent}
      showList={showList}
      isRefetching={isRefetching}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      currentPage={currentPage}
      numPages={numPages}
      onPageChange={handlePageChange}
      onRowClick={onRowClick}
      onToggleSelectAll={handleToggleSelectAll}
      onRetry={onRetry}
    />
  )
}

ReportAccessSecondaryCategoryListPresenter.displayName =
  'ReportAccessSecondaryCategoryListPresenter'
